import { useMutation } from "@apollo/client";
import { CheckCircleIcon as CheckCircleIconOutline } from "@heroicons/react/outline";
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/solid";
import { MouseEventHandler } from "react";

import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import { topicState } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { showConfetti } from "@helpers/helpers";

import toggleTopicStateMutation from "./graphql/toggle-topic-state-mutation";

const TopicCTA = ({
  topic,
  size = "5",
  className = "",
  isStatic = false,
}: {
  topic: any;
  size?: string;
  className?: string;
  isStatic?: boolean;
}) => {
  const [toggleState] = useMutation(toggleTopicStateMutation);

  const handleClickToggle: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (isStatic) return;
    const newState =
      topic.state === topicState.closed ? topicState.open : topicState.closed;
    toggleState({
      variables: {
        topicId: topic.id,
        state: newState,
      },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: {
            ...topic,
            state: newState,
          },
        },
      },
    });
  };

  const isDiscussed = topic.state === topicState.closed;
  const Icon = isDiscussed ? (
    <CheckCircleIcon className={`w-${size} h-${size}`} aria-label="Topic" />
  ) : (
    <CheckCircleIconOutline
      className={`w-${size} h-${size}`}
      aria-label="Topic"
    />
  );

  const iconColor = isDiscussed ? "blue-600" : "gray-400";

  return isStatic ? (
    <span
      className={classNames(
        `flex rounded-full items-center justify-center text-${iconColor} w-${size} h-${size}`,
        className,
      )}
    >
      {Icon}
    </span>
  ) : (
    <Button
      size={ButtonSize.medium}
      disabled={isStatic}
      onClick={(e) => {
        if (!isDiscussed) {
          showConfetti({
            confettiNumber: 40,
            confettiRadius: 4,
            confettiColors: ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0"],
          });
        }
        handleClickToggle(e);
      }}
      className={classNames(
        isDiscussed && "border-transparent bg-emerald-50 text-emerald-600",
        isDiscussed && !isStatic && "hover:bg-emerald-100",
        !isDiscussed && "invisible",
        !isDiscussed && !isStatic && "group-hover/topic:visible",
      )}
    >
      <CheckIcon className="h-4 w-4 shrink-0" />
      Discussed
    </Button>
  );
};

export default TopicCTA;
